import './TenantSwitcher.css'

import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactElement, Ref } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '#design-system/component/Avatar/Avatar'
import { Badge } from '#design-system/component/Badge/Badge'
import { Button } from '#design-system/component/Button/Button'
import { FlyOut } from '#design-system/component/FlyOut/FlyOut'
import { Divider } from '#design-system/component/Layout/Divider/Divider'
import { CaretDown } from '#design-system/icons/caret-down'
import { Check } from '#design-system/icons/check'
import { Gear } from '#design-system/icons/gear'
import { SignOut } from '#design-system/icons/sign-out'
import { UserPlus } from '#design-system/icons/user-plus'

/** Placement shape — to be replaced with the actual API data format once the data model is
 * finalised. `initials` will either be supplied as an explicit icon or derived programmatically
 * from the tenant name; it will not exist as a standalone field in the real contract. */
export interface TenantSwitcherTenant {
  id: string
  initials: string
  name: string
}

/** Placement shape — to be replaced with the actual API data format once the data model is
 * finalised. `initials` will either be supplied as an explicit icon or derived programmatically
 * from the workspace name; it will not exist as a standalone field in the real contract. */
export interface NavWorkspaceNode {
  id: string
  initials: string
  name: string
  isActive?: boolean | undefined
  children?: NavWorkspaceNode[] | undefined
}

interface NavMember {
  name: string
  role: string
  email: string
}

interface TenantSwitcherProps {
  tenant: TenantSwitcherTenant
  memberCount: number
  member: NavMember
  workspaceTree: NavWorkspaceNode[]
  onSettings: () => void
  onInviteMembers: () => void
  onSwitchWorkspace: (id: string) => void
  onLogOut: () => void
  className?: string | undefined
}

interface TenantSwitcherTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isOpen: boolean
  tenant: TenantSwitcherTenant
  ref?: Ref<HTMLButtonElement> | undefined
}

const TenantSwitcherTrigger = ({
  className,
  isOpen,
  tenant,
  type = 'button',
  ...props
}: TenantSwitcherTriggerProps): ReactElement => (
  <button
    {...props}
    className={clsx('tenant-switcher-trigger', isOpen && 'open', className)}
    type={type}
  >
    <Avatar name={tenant.name} size="sm" type="entity" />
    <span className="name">{tenant.name}</span>
    <CaretDown className="chevron" />
  </button>
)

interface WorkspaceItemProps {
  node: NavWorkspaceNode
  onSwitch: (id: string) => void
}

const WorkspaceItem = ({ node, onSwitch }: WorkspaceItemProps): ReactElement => {
  const children = node.children ?? []
  const hasChildren = children.length > 0

  return (
    <div className="workspace-node">
      <button
        className="workspace-item"
        onClick={() => {
          onSwitch(node.id)
        }}
        type="button"
      >
        <Avatar name={node.name} size="sm" type="entity" />
        <span className={clsx('workspace-name', node.isActive === true && 'active')}>
          {node.name}
        </span>
        {node.isActive === true && <Check className="workspace-check" />}
      </button>
      {hasChildren && (
        <div className="workspace-children">
          {children.map((child) => (
            <WorkspaceItem key={child.id} node={child} onSwitch={onSwitch} />
          ))}
        </div>
      )}
    </div>
  )
}

export const TenantSwitcher = ({
  className,
  member,
  memberCount,
  onInviteMembers,
  onLogOut,
  onSettings,
  onSwitchWorkspace,
  tenant,
  workspaceTree,
}: TenantSwitcherProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()

  return (
    <FlyOut
      aria-label={t('shell.nav.flyout_panel_label', { name: tenant.name })}
      className={clsx('tenant-switcher-panel', className)}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      role="dialog"
      trigger={{ element: <TenantSwitcherTrigger isOpen={isOpen} tenant={tenant} /> }}
    >
      <div>
        <div className="header">
          <div className="identity">
            <Avatar name={tenant.name} type="entity" />
            <div className="info">
              <span className="name">{tenant.name}</span>
              <span className="count">
                {t('shell.nav.tenant_member_count', { count: memberCount })}
              </span>
            </div>
          </div>
          <div className="actions">
            <Button
              actionRole="secondary"
              icon={<Gear />}
              label={t('shell.nav.settings')}
              onClick={() => {
                setIsOpen(false)
                onSettings()
              }}
              size="small"
            />
            <Button
              actionRole="secondary"
              icon={<UserPlus />}
              label={t('shell.nav.invite_members')}
              onClick={onInviteMembers}
              size="small"
            />
          </div>
        </div>

        <Divider />

        <div className="workspaces">
          <span className="section-label">{t('shell.nav.switch_workspace')}</span>
          <div className="tree">
            {workspaceTree.map((node) => (
              <WorkspaceItem key={node.id} node={node} onSwitch={onSwitchWorkspace} />
            ))}
          </div>
        </div>

        <Divider />

        <div className="member">
          <Avatar name={member.name} type="person" />
          <span className="member-details">
            <span className="member-name-row">
              <span className="member-name">{member.name}</span>
              <Badge label={member.role} />
            </span>
            <span className="member-email">{member.email}</span>
          </span>
        </div>

        <Divider />

        <div className="logout">
          <Button
            actionRole="ghost"
            className="logout-btn"
            icon={<SignOut />}
            label={t('shell.nav.log_out')}
            onClick={() => {
              setIsOpen(false)
              onLogOut()
            }}
          />
        </div>
      </div>
    </FlyOut>
  )
}
